/**
 * Form validation utilities
 * Common validation functions that can be reused across components
 */

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {string} - Error message if invalid, empty string if valid
 */
export const validateEmail = (email) => {
  if (!email.trim()) {
    return 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Please enter a valid email address';
  }
  return '';
};

/**
 * Validates password
 * @param {string} password - Password to validate
 * @param {number} minLength - Minimum password length (default: 6)
 * @returns {string} - Error message if invalid, empty string if valid
 */
export const validatePassword = (password, minLength = 6) => {
  if (!password) {
    return 'Password is required';
  } else if (password.length < minLength) {
    return `Password must be at least ${minLength} characters long`;
  }
  return '';
};

/**
 * Validates required field
 * @param {string} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string} - Error message if invalid, empty string if valid
 */
export const validateRequired = (value, fieldName = 'This field') => {
  if (!value || !value.trim()) {
    return `${fieldName} is required`;
  }
  return '';
};

/**
 * Validates phone number format (basic validation)
 * @param {string} phone - Phone number to validate
 * @returns {string} - Error message if invalid, empty string if valid
 */
export const validatePhone = (phone) => {
  if (!phone.trim()) {
    return 'Phone number is required';
  } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/[\s\-\(\)]/g, ''))) {
    return 'Please enter a valid phone number';
  }
  return '';
};

/**
 * Generic input change handler that clears field-specific errors
 * @param {Function} setValue - State setter for the input value
 * @param {Function} setErrors - State setter for form errors
 * @param {string} fieldName - Name of the field being updated
 * @returns {Function} - Handler function for input onChange
 */
export const createInputChangeHandler = (setValue, setErrors, fieldName) => {
  return (value) => {
    setValue(value);
    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[fieldName]) {
        return { ...prev, [fieldName]: '' };
      }
      return prev;
    });
  };
};

/**
 * Generic form validation that runs multiple validators
 * @param {Object} formData - Form data object
 * @param {Object} validators - Object with field names as keys and validator functions as values
 * @returns {Object} - Object with field names as keys and error messages as values
 */
export const validateForm = (formData, validators) => {
  const errors = {};
  
  Object.keys(validators).forEach(field => {
    const validator = validators[field];
    const value = formData[field];
    const error = validator(value);
    
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};

/**
 * Common form validators object for reuse
 */
export const commonValidators = {
  email: validateEmail,
  password: validatePassword,
  required: validateRequired,
  phone: validatePhone,
};
